import React, { useState, useEffect } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { initializeApp } from "firebase/app";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faUsers,
  faShield,
  faBullseye,
  faSatelliteDish,
  faInfoCircle,
  faArrowRightFromBracket,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Table,
  Form,
  OverlayTrigger,
  Tooltip as BSTooltip,
} from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

// Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyBBzepSPN2wdZfvan5GgyeeIREAV6k5BaU",
  authDomain: "loftie-4f472.firebaseapp.com",
  databaseURL: "https://loftie-4f472.firebaseio.com",
  projectId: "loftie-4f472",
  storageBucket: "loftie-4f472.appspot.com",
  messagingSenderId: "302939981606",
  appId: "1:302939981606:web:7fcb10ed6fe46fa437a3e7",
  measurementId: "G-L8VGQXX28P",
};

const app = initializeApp(firebaseConfig);
const authInstance = getAuth(app);
const db = getFirestore(app);

// Palette (matches SubscriptionAnalytics)
const C = {
  peach: "#F0D6B5",
  peachDark: "#E7AA7E",
  brown: "#635956",
  brownDark: "#3D3D3D",
  warmWhite: "#FBF7ED",
  green: "#10B981",
  coral: "#DA7833",
  red: "#CF6679",
  gray: "#9CA3AF",
  darkGray: "#4F4F4F",
  blue: "#4E6078",
  purple: "#312C6C",
  grid: "#E4E4E4",
  bg: "#FBF7ED",
  light: "#F6EFDF",
  white: "#FFFFFF",
  text: "#252525",
};

const FEATURE_COLOR = {
  rest: C.blue,
  focus: C.coral,
  drift: C.purple,
  none: C.gray,
};

const FEATURE_LABEL = {
  rest: "REST",
  focus: "Focus",
  drift: "Drift",
};

const fmt = {
  number: (v) => (v == null ? "0" : v.toLocaleString("en-US")),
  pct: (v) => (v == null ? "0%" : `${v.toFixed(2)}%`),
};

function staleness(computedAt) {
  if (!computedAt) return { label: "Unknown", color: C.gray, ago: "" };
  const d = new Date(computedAt);
  const hrs = (new Date() - d) / 3.6e6;
  const days = hrs / 24;
  const ago =
    hrs < 1
      ? `${Math.round(hrs * 60)}m ago`
      : hrs < 24
      ? `${Math.round(hrs)}h ago`
      : `${Math.round(days)}d ago`;
  const color = days < 1 ? C.green : days < 3 ? C.coral : C.red;
  const label = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return { label, color, ago };
}

function Info({ text }) {
  return (
    <OverlayTrigger placement="top" overlay={<BSTooltip>{text}</BSTooltip>}>
      <FontAwesomeIcon
        icon={faInfoCircle}
        style={{ color: C.gray, marginLeft: 6, fontSize: 12, cursor: "help" }}
      />
    </OverlayTrigger>
  );
}

function KPICard({ icon, label, value, detail, color, info }) {
  return (
    <Card
      style={{
        borderRadius: 12,
        border: "none",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        height: "100%",
      }}
    >
      <Card.Body style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
          <FontAwesomeIcon
            icon={icon}
            style={{ color: color || C.peach, marginRight: 8, fontSize: 13 }}
          />
          <span
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: C.gray,
              fontWeight: 600,
            }}
          >
            {label}
          </span>
          {info && <Info text={info} />}
        </div>
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: C.brown,
            lineHeight: 1.2,
          }}
        >
          {value}
        </div>
        {detail && (
          <div
            style={{
              fontSize: 12,
              color: C.darkGray,
              marginTop: 6,
              lineHeight: 1.4,
            }}
          >
            {detail}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function Section({ title, info, right, children }) {
  return (
    <Card
      style={{
        borderRadius: 12,
        border: "none",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        marginBottom: 24,
      }}
    >
      <Card.Body style={{ padding: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h6 style={{ color: C.brown, fontWeight: 600, margin: 0 }}>
            {title}
            {info && <Info text={info} />}
          </h6>
          {right}
        </div>
        {children}
      </Card.Body>
    </Card>
  );
}

function ChartTip({ active, payload, label, suffix }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.grid}`,
        borderRadius: 8,
        padding: "8px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        fontSize: 13,
      }}
    >
      <div style={{ color: C.gray, marginBottom: 4, fontSize: 12 }}>
        {label}
      </div>
      {payload.map((e, i) => (
        <div key={i} style={{ color: e.color }}>
          {e.name}: {fmt.number(e.value)}
          {suffix || ""}
        </div>
      ))}
    </div>
  );
}

function FeatureAnalytics() {
  const [user, userLoading] = useAuthState(authInstance);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState(null);

  const fetchAnalytics = async () => {
    try {
      const snap = await getDoc(doc(db, "feature-analytics", "latest"));
      if (snap.exists()) {
        setData(snap.data());
        setError(null);
      } else {
        setError(
          "No feature analytics data found. Run scripts/run_feature_analytics.py first."
        );
      }
    } catch (e) {
      setError("Failed to load: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user) fetchAnalytics();
  }, [user]);

  if (userLoading)
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "50vh" }}
      >
        <Spinner animation="border" style={{ color: C.peach }} />
      </Container>
    );

  if (!user)
    return (
      <div
        style={{
          background: C.bg,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          style={{
            width: 360,
            borderRadius: 12,
            border: "none",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Card.Body style={{ padding: 32 }}>
            <h4
              style={{
                color: C.peach,
                fontWeight: 700,
                marginBottom: 4,
                textAlign: "center",
              }}
            >
              <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 8 }} />
              Feature Analytics
            </h4>
            <p
              style={{
                color: C.gray,
                fontSize: 13,
                textAlign: "center",
                marginBottom: 24,
              }}
            >
              Sign in to continue
            </p>
            {loginError && (
              <Alert variant="danger" style={{ fontSize: 13 }}>
                {loginError}
              </Alert>
            )}
            <Form
              onSubmit={async (e) => {
                e.preventDefault();
                setLoginError(null);
                try {
                  await signInWithEmailAndPassword(
                    authInstance,
                    loginEmail,
                    loginPassword
                  );
                } catch (err) {
                  setLoginError(err.message);
                }
              }}
            >
              <Form.Group className="mb-3">
                <Form.Control
                  type="email"
                  placeholder="Email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Control
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              </Form.Group>
              <Button
                type="submit"
                style={{
                  width: "100%",
                  borderRadius: 8,
                  background: C.peach,
                  border: "none",
                  color: C.text,
                  fontWeight: 600,
                }}
              >
                Sign In
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </div>
    );

  if (loading)
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "50vh" }}
      >
        <Spinner animation="border" style={{ color: C.peach }} />
      </Container>
    );

  if (error && !data)
    return (
      <Container className="text-center mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  if (!data) return null;

  const {
    total_loftie_plus,
    adoption,
    adoption_pct,
    overlaps,
    intensity,
    computation_duration_seconds,
  } = data;
  const stale = staleness(data.computed_at);

  // Build combined "users by feature-count" split for pie chart
  const anyCount = adoption.any || 0;
  const singleFeatureCount = anyCount - (
    (overlaps.rest_and_focus || 0) +
    (overlaps.rest_and_drift || 0) +
    (overlaps.focus_and_drift || 0) -
    2 * (overlaps.all_three || 0)
  );
  // NOTE: overlaps are pairwise; the above is an approximation used only for the
  // distribution pie, not for exact per-user bucketing.
  const twoOrMore = anyCount - singleFeatureCount;

  const distributionData = [
    { name: "No features", value: adoption.none, fill: C.gray },
    {
      name: "1 feature",
      value: Math.max(singleFeatureCount, 0),
      fill: C.peach,
    },
    { name: "2+ features", value: Math.max(twoOrMore, 0), fill: C.coral },
  ];

  const adoptionBarData = [
    {
      feature: FEATURE_LABEL.rest,
      adopted: adoption.rest,
      pct: adoption_pct.rest,
      fill: FEATURE_COLOR.rest,
    },
    {
      feature: FEATURE_LABEL.focus,
      adopted: adoption.focus,
      pct: adoption_pct.focus,
      fill: FEATURE_COLOR.focus,
    },
    {
      feature: FEATURE_LABEL.drift,
      adopted: adoption.drift,
      pct: adoption_pct.drift,
      fill: FEATURE_COLOR.drift,
    },
  ];

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      <Container fluid style={{ padding: "24px 32px", maxWidth: 1400 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <div>
            <h3 style={{ color: C.brown, fontWeight: 700, marginBottom: 4 }}>
              <FontAwesomeIcon
                icon={faChartLine}
                style={{ marginRight: 10, color: C.peach }}
              />
              Loftie+ Feature Analytics
            </h3>
            <div style={{ fontSize: 13, color: C.gray }}>
              Computed {stale.label} ·{" "}
              <span style={{ color: stale.color, fontWeight: 600 }}>
                {stale.ago}
              </span>
              {computation_duration_seconds
                ? ` · took ${computation_duration_seconds}s`
                : ""}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, color: C.darkGray }}>
              {user.email}
            </span>
            <Button
              variant="light"
              size="sm"
              onClick={() => authInstance.signOut()}
              style={{ borderRadius: 8 }}
            >
              <FontAwesomeIcon icon={faArrowRightFromBracket} /> Sign out
            </Button>
          </div>
        </div>

        {/* KPI row */}
        <Row className="g-3 mb-4">
          <Col md={3}>
            <KPICard
              icon={faUsers}
              label="Loftie+ Users"
              value={fmt.number(total_loftie_plus)}
              detail="Non-internal emails only"
              color={C.peach}
              info="Users with loftie-plus-membership = true, excluding @byloftie.com addresses."
            />
          </Col>
          <Col md={3}>
            <KPICard
              icon={faShield}
              label="REST adopted"
              value={fmt.number(adoption.rest)}
              detail={fmt.pct(adoption_pct.rest)}
              color={FEATURE_COLOR.rest}
              info="restData.isSet = true and at least one app / category / website is blocked."
            />
          </Col>
          <Col md={3}>
            <KPICard
              icon={faBullseye}
              label="Focus adopted"
              value={fmt.number(adoption.focus)}
              detail={fmt.pct(adoption_pct.focus)}
              color={FEATURE_COLOR.focus}
              info="focusData.isSet = true and lastActivated is populated."
            />
          </Col>
          <Col md={3}>
            <KPICard
              icon={faSatelliteDish}
              label="Drift adopted"
              value={fmt.number(adoption.drift)}
              detail={fmt.pct(adoption_pct.drift)}
              color={FEATURE_COLOR.drift}
              info="proximityData.isEnabled = true and at least one device is armed."
            />
          </Col>
        </Row>

        <Row className="g-3 mb-4">
          <Col md={4}>
            <KPICard
              icon={faChartLine}
              label="Any feature"
              value={fmt.number(adoption.any)}
              detail={fmt.pct(adoption_pct.any)}
              color={C.green}
            />
          </Col>
          <Col md={4}>
            <KPICard
              icon={faChartLine}
              label="All three features"
              value={fmt.number(adoption.all_three)}
              detail={
                total_loftie_plus
                  ? `${(
                      (100 * adoption.all_three) /
                      total_loftie_plus
                    ).toFixed(2)}%`
                  : "0%"
              }
              color={C.coral}
            />
          </Col>
          <Col md={4}>
            <KPICard
              icon={faExclamationTriangle}
              label="No features (churn risk)"
              value={fmt.number(adoption.none)}
              detail={fmt.pct(adoption_pct.none)}
              color={C.red}
              info="Loftie+ members not using REST, Focus, or Drift. Your retention target."
            />
          </Col>
        </Row>

        {/* Adoption bar chart */}
        <Section
          title="Adoption by feature"
          info="Count of Loftie+ users currently using each feature."
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={adoptionBarData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
              <XAxis
                dataKey="feature"
                tick={{ fill: C.darkGray, fontSize: 12 }}
              />
              <YAxis tick={{ fill: C.darkGray, fontSize: 12 }} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="adopted" name="Users adopted" radius={[6, 6, 0, 0]}>
                {adoptionBarData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Section>

        {/* Distribution + overlaps */}
        <Row className="g-3 mb-4">
          <Col md={6}>
            <Section title="Feature-count distribution">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={distributionData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={(e) => `${e.name}: ${fmt.number(e.value)}`}
                  >
                    {distributionData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Section>
          </Col>
          <Col md={6}>
            <Section
              title="Pairwise overlaps"
              info="Number of users adopting each pair of features (and all three)."
            >
              <Table
                size="sm"
                borderless
                style={{ marginBottom: 0, fontSize: 14 }}
              >
                <tbody>
                  <tr>
                    <td style={{ color: C.darkGray }}>REST ∩ Focus</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {fmt.number(overlaps.rest_and_focus)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: C.darkGray }}>REST ∩ Drift</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {fmt.number(overlaps.rest_and_drift)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: C.darkGray }}>Focus ∩ Drift</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {fmt.number(overlaps.focus_and_drift)}
                    </td>
                  </tr>
                  <tr style={{ borderTop: `1px solid ${C.grid}` }}>
                    <td style={{ color: C.brown, fontWeight: 600 }}>
                      All three
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 700,
                        color: C.coral,
                      }}
                    >
                      {fmt.number(overlaps.all_three)}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Section>
          </Col>
        </Row>

        {/* Intensity */}
        <Section
          title="Intensity (adopted users only)"
          info="Averages across users who have actually adopted the feature."
        >
          <Row>
            <Col md={4}>
              <h6
                style={{
                  color: FEATURE_COLOR.rest,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                <FontAwesomeIcon
                  icon={faShield}
                  style={{ marginRight: 6 }}
                />{" "}
                REST
              </h6>
              <Table size="sm" borderless style={{ fontSize: 13 }}>
                <tbody>
                  <tr>
                    <td style={{ color: C.darkGray }}>Avg blocked items</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {intensity.rest.avg_blocked_items}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: C.darkGray }}>Avg active days/wk</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {intensity.rest.avg_active_days}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={4}>
              <h6
                style={{
                  color: FEATURE_COLOR.focus,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                <FontAwesomeIcon
                  icon={faBullseye}
                  style={{ marginRight: 6 }}
                />{" "}
                Focus
              </h6>
              <Table size="sm" borderless style={{ fontSize: 13 }}>
                <tbody>
                  <tr>
                    <td style={{ color: C.darkGray }}>Avg blocked items</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {intensity.focus.avg_blocked_items}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: C.darkGray }}>
                      Avg longest focus (min)
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {intensity.focus.avg_longest_min}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={4}>
              <h6
                style={{
                  color: FEATURE_COLOR.drift,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                <FontAwesomeIcon
                  icon={faSatelliteDish}
                  style={{ marginRight: 6 }}
                />{" "}
                Drift
              </h6>
              <Table size="sm" borderless style={{ fontSize: 13 }}>
                <tbody>
                  <tr>
                    <td style={{ color: C.darkGray }}>Avg devices armed</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {intensity.drift.avg_devices_armed}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: C.darkGray }}>Avg total sessions</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {intensity.drift.avg_total_sessions}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: C.darkGray }}>
                      Avg longest session (min)
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {intensity.drift.avg_longest_min}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Section>
      </Container>
    </div>
  );
}

export default FeatureAnalytics;
